import React, { useState, useEffect, useCallback } from 'react';
import { notesApi } from '../../api/notes';
import toast from 'react-hot-toast';
import { Pin, Send, Trash2, Edit3 } from 'lucide-react';
import WidgetCard from './WidgetCard';
import WidgetModal from './WidgetModal';

export default function NotesWidget({ recordId }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchNotes = useCallback(async () => {
    try {
      const data = await notesApi.getForRecord(recordId);
      setNotes(data.notes);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [recordId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setSending(true);
    try {
      await notesApi.create({ record_id: recordId, content: content.trim() });
      setContent('');
      fetchNotes();
    } catch (err) { toast.error('Failed to add note'); }
    finally { setSending(false); }
  };

  const handlePin = async (note) => {
    try { await notesApi.update(note.id, { pinned: !note.pinned }); fetchNotes(); }
    catch (err) { toast.error('Failed'); }
  };

  const handleDelete = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    try { await notesApi.delete(note.id); fetchNotes(); }
    catch (err) { toast.error('Failed'); }
  };

  const formatDate = (d) => {
    const diff = Date.now() - new Date(d).getTime();
    if (diff < 60000) return 'now';
    if (diff < 3600000) return Math.floor(diff / 60000) + 'm';
    if (diff < 86400000) return Math.floor(diff / 3600000) + 'h';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const NoteItem = ({ note, compact }) => (
    <div className={`px-4 py-2.5 group hover:bg-gray-50 ${note.pinned ? 'bg-yellow-50/50' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-medium text-gray-700">{note.first_name}</span>
            <span className="text-[10px] text-gray-400">{formatDate(note.created_at)}</span>
            {note.pinned && <Pin className="h-2.5 w-2.5 text-yellow-500 fill-yellow-500" />}
          </div>
          <p className={`text-xs text-gray-600 whitespace-pre-wrap break-words ${compact ? 'line-clamp-2' : ''}`}>{note.content}</p>
        </div>
        {!compact && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
            <button onClick={() => handlePin(note)} className={`p-1 rounded hover:bg-gray-200 ${note.pinned ? 'text-yellow-500' : 'text-gray-400'}`}>
              <Pin className="h-3 w-3" />
            </button>
            <button onClick={() => handleDelete(note)} className="p-1 rounded hover:bg-gray-200 text-gray-400 hover:text-red-500">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const quickAdd = (
    <form onSubmit={handleSubmit} className="flex gap-2 px-3 py-2">
      <input value={content} onChange={e => setContent(e.target.value)} placeholder="Quick note..."
        disabled={sending} className="flex-1 text-xs px-2.5 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
      <button type="submit" disabled={!content.trim() || sending}
        className="p-1.5 text-brand-600 hover:bg-brand-50 rounded-lg disabled:opacity-30">
        <Send className="h-3.5 w-3.5" />
      </button>
    </form>
  );

  return (
    <>
      <WidgetCard title="Notes" icon="📝" count={notes.length} onExpand={() => setModalOpen(true)} footer={quickAdd}>
        {loading ? <p className="text-xs text-gray-400 p-4">Loading...</p> :
          notes.length === 0 ? <p className="text-xs text-gray-400 text-center py-4">No notes yet</p> :
          <div className="divide-y divide-gray-50">
            {notes.slice(0, 3).map(n => <NoteItem key={n.id} note={n} compact />)}
            {notes.length > 3 && <p className="text-[10px] text-gray-400 text-center py-2">+{notes.length - 3} more</p>}
          </div>
        }
      </WidgetCard>
      <WidgetModal open={modalOpen} onClose={() => setModalOpen(false)} title="Notes" icon="📝" width="max-w-xl">
        <div className="divide-y divide-gray-50 max-h-[50vh] overflow-y-auto">
          {notes.map(n => <NoteItem key={n.id} note={n} />)}
          {notes.length === 0 && <p className="text-sm text-gray-400 text-center py-8">No notes yet</p>}
        </div>
        <div className="border-t border-gray-200 p-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input value={content} onChange={e => setContent(e.target.value)} placeholder="Add a note..."
              disabled={sending} className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500" />
            <button type="submit" disabled={!content.trim() || sending}
              className="px-4 py-2 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 disabled:opacity-30">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      </WidgetModal>
    </>
  );
}
